import { UNASSIGNED, decodeTaskAssignee } from '@/helpers/taskAssignee';
import { GetTasksInput, TaskAssigneeFilterInput } from '@/gql/graphql';

/**
 * The shared advanced-filter control stores every selection as a flat list of
 * strings, so an assignee selection is held as the pickers' assignee token and
 * mapped to the typed `assignees` input before the request goes out.
 *
 * `UNASSIGNED` is checked first, since the codec reads it as "no assignee" and
 * so returns null for it just as it does for a token it cannot parse.
 */
const toFilterInput = (token: string): TaskAssigneeFilterInput | null => {
  if (token === UNASSIGNED) return { unassigned: true };

  const assignee = decodeTaskAssignee(token);
  if (!assignee) return null;

  return assignee.type === 'user'
    ? { userId: assignee.userPublicId }
    : { teamId: assignee.teamPublicId };
};

/**
 * Read a persisted assignee selection, keeping only what this encoding can
 * parse.
 *
 * A selection saved before the tokens existed names a bare user id. Left in, it
 * survives as far as `withTypedAssignees`, which drops it — so the request
 * carries no assignee filter and the list widens to every visible task, while
 * the control still shows the filter as applied. Dropping it here instead keeps
 * the two in agreement.
 */
export const storedAssigneeFilters = (stored: string | null): string[] =>
  stored?.split(',').filter((token) => toFilterInput(token) !== null) ?? [];

/**
 * Swap the UI's `assignedTo` tokens for the typed `assignees` input. The
 * deprecated `assignedTo` field is never sent — unrecognised tokens are dropped
 * rather than passed on.
 */
export const withTypedAssignees = (
  input?: GetTasksInput
): GetTasksInput | undefined => {
  if (!input) return input;

  const { assignedTo, ...rest } = input;
  if (!assignedTo?.length) return rest;

  const assignees = assignedTo
    .map(toFilterInput)
    .filter((a): a is TaskAssigneeFilterInput => a !== null);

  return assignees.length ? { ...rest, assignees } : rest;
};
